/**
 * RequireAuth — guard component for private routes.
 *
 * Async (feature 004 US2): consulta GET /auth/me al montar y al
 * cambiar la ruta. Mientras carga, renderiza un skeleton; si la
 * sesión es null, redirige a /login preservando state.from; si
 * existe, renderiza children.
 *
 * Contrato: specs/004-go-live/contracts/auth-real-contract.md
 */
import { type ReactElement, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";

import { RouteSkeleton } from "../../components/RouteSkeleton";
import { getSession } from "../../features/login/lib/auth";

export interface LoginLocationState {
  from?: {
    pathname: string;
    search?: string;
    hash?: string;
  };
}

interface RequireAuthProps {
  children: ReactElement;
}

type AuthState = { status: "checking" } | { status: "ok" } | { status: "ko" };

export function RequireAuth({ children }: RequireAuthProps): ReactElement {
  const location = useLocation();
  const [state, setState] = useState<AuthState>({ status: "checking" });

  // Solo validar sesión una vez al MOUNT. El sliding-TTL del backend
  // mantiene viva la cookie en cada hit autenticado; si expira, la
  // siguiente request retornará 401 y el handler redirige a /login.
  // No re-checkear en cada cambio de pathname — generaba skeleton
  // entre navegación, percibido como "refresh" de la página.
  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (cancelled) return;
        setState({ status: session ? "ok" : "ko" });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: "ko" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "checking") {
    return <RouteSkeleton variant="app" />;
  }
  if (state.status === "ko") {
    const from: LoginLocationState["from"] = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
    return <Navigate to="/login" replace state={{ from } satisfies LoginLocationState} />;
  }
  return children;
}
